import { useQuery } from '@apollo/client';

import { Empty } from 'antd';
import { Summary } from './summary';
import {
  CasesSendReportCaseSummaryContainerArchivedSendReportCaseSummaryDocument,
  FileInfo,
  ArchivedCaseSummaryFinanceDetailsAdhocTransaction,
  CasesSendReportCaseSummaryContainerArchivedSendReportCaseSummaryCaseDetailsFieldsFragment,
  CasesSendReportCaseSummaryContainerFileInfoFieldsFragment,
  ArchivedCaseSummaryActivityLog,
  CasesSendReportCaseSummaryContainerArchivedSendReportCaseSummaryCaseDetailsApplicationReviewPrivateCaseDetailsFieldsFragment,
  CasesSendReportCaseSummaryContainerArchivedCaseSummaryFinanceDetailsAdhocTransactionFieldsFragment,
  ArchivedCaseSummaryFinanceDetailsTransactions
} from '../../../../../../generated';
import { lookupCountryQuery } from './send-report-archive-country-query';
import { Country } from '../../../../../../types/country';
import { ComponentQueryLoader } from '../../../../../shared/component-query-loader';
import { TransactionData } from '../../../../../shared/transactions/transactions-lite';

interface SummaryContainerProps {
  archivedCaseId: string;
}

export const SummaryContainer: React.FC<SummaryContainerProps> = (props) => {
  const { data, loading, error } = useQuery(CasesSendReportCaseSummaryContainerArchivedSendReportCaseSummaryDocument, {
    variables: {
      archivedSendReportCaseSummaryId: props.archivedCaseId
    }
  });

  const { data: countryData, loading: countryLoading, error: countryError } = useQuery(lookupCountryQuery);

  function getAdditionalFiles(files: FileInfo[]): FileInfo[] {
    return files.filter((file) => file !== null && file.name.includes('/additional-private-files/'));
  }

  function computeFinanceTotals(finance: ArchivedCaseSummaryFinanceDetailsTransactions) {
    let totalSent = 0;
    let totalReceived = 0;

    if (finance?.adhocTransactions?.length) {
      finance.adhocTransactions
        .filter((transaction) => {
          const { approval, transactionReference } = transaction as ArchivedCaseSummaryFinanceDetailsAdhocTransaction;
          return !((approval?.isApplicantApprovalRequired === true && approval?.isApplicantApproved !== true) || transactionReference?.isSuccess === false);
        })
        .forEach((transaction) => {
          const amount = transaction?.amount;
          if (amount !== undefined && amount !== null) {
            if (amount > 0) {
              totalSent += amount;
            } else if (amount < 0) {
              totalReceived += Math.abs(amount); // Add to total received if negative
            }
          }
        });
    }

    if (finance?.submission) {
      totalSent += finance.submission.amount ?? 0;
    }

    return { totalSent, totalReceived };
  }

  // Filtering files
  let additionalFiles: FileInfo[] = [];
  if (data?.archivedSendReportCaseSummary?.files?.length) {
    additionalFiles = getAdditionalFiles(data.archivedSendReportCaseSummary?.files as FileInfo[]);
  }

  let totalSentAmount = 0;
  let totalReceivedAmount = 0;

   // Processing finance details
   if (data?.archivedSendReportCaseSummary?.financeDetails?.transactions) {
    const finance = data.archivedSendReportCaseSummary.financeDetails.transactions;
    if (finance) {
      const { totalSent, totalReceived } = computeFinanceTotals(finance as ArchivedCaseSummaryFinanceDetailsTransactions);
      totalSentAmount += totalSent;
      totalReceivedAmount += totalReceived;
    }
  }

  const submissionTransaction: ArchivedCaseSummaryFinanceDetailsAdhocTransaction = {
    amount: data?.archivedSendReportCaseSummary?.financeDetails?.transactions?.submission?.amount ?? undefined,
    reason: `Intealth Requested for ID Verification`,
    transactionReference: {
      referenceId: data?.archivedSendReportCaseSummary?.financeDetails?.transactions?.submission?.transactionReference?.referenceId,
      completedAt: data?.archivedSendReportCaseSummary?.financeDetails?.transactions?.submission?.transactionReference?.completedAt
    }
  };

  let adhocTransactions: CasesSendReportCaseSummaryContainerArchivedCaseSummaryFinanceDetailsAdhocTransactionFieldsFragment[] = [];

  if (Array.isArray(data?.archivedSendReportCaseSummary?.financeDetails?.transactions?.adhocTransactions)) {
    adhocTransactions = data?.archivedSendReportCaseSummary?.financeDetails.transactions.adhocTransactions.filter(
      (transaction): transaction is CasesSendReportCaseSummaryContainerArchivedCaseSummaryFinanceDetailsAdhocTransactionFieldsFragment => {
        const approval = transaction?.approval;
        const transactionRef = transaction?.transactionReference;
        return !!transactionRef && (!approval || approval.isApplicantApprovalRequired !== true || approval.isApplicantApproved === true) && transactionRef.isSuccess !== false;
      }
    );
  }

  const allTransactions = [...adhocTransactions, submissionTransaction];
  const transactionData: TransactionData = {
    totalReceived: totalReceivedAmount,
    totalSent: totalSentAmount,
    transactionsData: allTransactions
  };

  return (
    <ComponentQueryLoader
      loading={loading || countryLoading}
      error={error || countryError}
      hasData={!!data?.archivedSendReportCaseSummary && Object.keys(data.archivedSendReportCaseSummary).length > 0 && !!countryData}
      hasDataComponent={
        <Summary
          caseDetailsData={data?.archivedSendReportCaseSummary?.caseDetails as CasesSendReportCaseSummaryContainerArchivedSendReportCaseSummaryCaseDetailsFieldsFragment}
          fileDetails={data?.archivedSendReportCaseSummary?.files as CasesSendReportCaseSummaryContainerFileInfoFieldsFragment[]}
          countryData={countryData?.countries?.eventPayload?.countries as Country[]}
          transactionData={transactionData}
          privateCaseDetails={
            data?.archivedSendReportCaseSummary?.caseDetails?.applicationReview
              ?.privateCaseDetails as CasesSendReportCaseSummaryContainerArchivedSendReportCaseSummaryCaseDetailsApplicationReviewPrivateCaseDetailsFieldsFragment
          }
          activityData={data?.archivedSendReportCaseSummary?.activityLog as ArchivedCaseSummaryActivityLog[]}
          additionalFiles={additionalFiles}
        />
      }
      noDataComponent={<Empty description={`No archived summary available for case ID: ${props.archivedCaseId}`} />}
    />
  );
};
